import { GraphQLClient } from "graphql-request";
import { getSdk, type SdkFunctionWrapper } from "./graphql/generated";
import { getDeviceHeaders } from "./device";
import { Logger } from "./logger";
import { datadogError } from "./monitoring/datadog";

const LOG_TAG = "HERMES";

/**
 * SDK wrapper that intercepts all GraphQL operations, logging request
 * status and duration via Logger, with error reporting to Datadog.
 */
const requestInterceptor: SdkFunctionWrapper = async (
  action,
  operationName,
  operationType,
) => {
  const kind = operationType ?? "query";
  const start = performance.now();
  Logger.info(LOG_TAG, `→ ${kind} "${operationName}" started`);

  try {
    const result = await action();
    const ms = Math.round(performance.now() - start);
    Logger.info(LOG_TAG, `✓ ${kind} "${operationName}" OK (${ms}ms)`);
    return result;
  } catch (error) {
    const ms = Math.round(performance.now() - start);
    Logger.error(LOG_TAG, `✗ ${kind} "${operationName}" FAILED (${ms}ms)`, error);
    datadogError('HERMES', error, {
      operationName,
      operationType,
    });
    throw error;
  }
};

/**
 * Hermes is the GraphQL client for communicating with the Hermes backend API.
 * It provides an auto-generated SDK (from graphql-codegen) to call queries and mutations,
 * as well as helper methods to configure custom headers like Authorization.
 *
 * Example:
 *
 * ```ts
 * Hermes.setDefaultHeaders();
 * Hermes.setAuthToken('user-token');
 * const result = await Hermes.client.GetUser();
 * console.log(`User: ${result.user.name}`);
 * ```
 */
export class Hermes {
  private static graphQLClient = new GraphQLClient(
    "https://hermes.brasilparalelo.com.br/api"
  );

  static client = getSdk(Hermes.graphQLClient, requestInterceptor);

  static setHeader(key: string, value: string) {
    Hermes.graphQLClient.setHeader(key, value);
  }

  static setHeaders(headers: Record<string, string>) {
    Hermes.graphQLClient.setHeaders(headers);
  }

  static setAuthToken(token: string) {
    Hermes.graphQLClient.setHeader("Authorization", `Bearer ${token}`);
  }

  /**
   * Sets the standard BP device headers on the GraphQL client.
   *
   * Must be called after the Cast context is ready (device detection
   * depends on `CastReceiverContext`). Uses individual `setHeader` calls
   * so existing headers (e.g. Authorization) are preserved.
   */
  static setDefaultHeaders() {
    const headers = getDeviceHeaders();
    for (const [key, value] of Object.entries(headers)) {
      Hermes.graphQLClient.setHeader(key, value);
    }
    Logger.debug(LOG_TAG, "Default device headers set", headers);
  }
}
